'use client';

import Link from '@eliadmin/core/src/components/link';

import type { FormValidationMessages } from '@eliadmin/core/src/hooks/use-form-validation-messages';
import type { Country } from '@eliadmin/core/src/api/general/types/general-types';
import type { UseFormReturn } from 'react-hook-form';

import { register } from '@eliadmin/core/src/api/customers/customers-api';
import type { SocialAuthProviderItem } from '@eliadmin/core/src/api/customers/customer-social-auth-api';
import { ValidationError } from '@eliadmin/core/src/api/errors';

import { SocialAuthButtons } from '@eliadmin/core/src/components/auth/social-auth-buttons';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { Checkbox } from '@eliadmin/core/src/components/ui/checkbox';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@eliadmin/core/src/components/ui/form';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { env } from '@eliadmin/core/src/env';
import { useFormValidationMessages } from '@eliadmin/core/src/hooks/use-form-validation-messages';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { setAuthToken } from '@eliadmin/core/src/server/api-client';
import { initCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { useRouter } from 'next/navigation';
import { useState } from 'react';

import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { getDefaultUrlForTosOrGdprLocalized } from '@eliadmin/core/src/utils/misc-utils';
import { Paragraph } from '../ui/paragraph';
import { CacheKey } from '@eliadmin/core/src/api/constants';
import { fetchCountries } from '@eliadmin/core/src/api/general/general-api';
import { useQuery } from '@tanstack/react-query';
import { Heading } from '../ui/heading';
import { RegisterSuccess } from './register-success';
import { Field } from '../form/labeled-field';
import { InputImage } from '../ui/input-image';

import Image from 'next/image';
import { toast } from '@eliadmin/core/src/components/ui/use-toast';
import { DialogClose } from '../ui/dialog';
import { LuX } from 'react-icons/lu';

function Separator({ className }: { className?: string }) {
  return <div className={cn('h-[1px] w-full bg-border', className)} />;
}

export type RegisterFormValues = z.infer<ReturnType<typeof registerSchema>>;

export const registerSchema = (VM: FormValidationMessages, passwordRuleMessage?: string) =>
  z
    .object({
      name: z.string().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
      surname: z.string().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
      email: z.string().email(VM.EMAIL_INVALID_VALIDATION_MESSAGE()),
      // min(12): the phone input pre-fills the dial code ("+421"), same rule as in checkout.
      phone: z.string().min(12, VM.CANT_BE_EMPTY_PHONE_VALIDATION_MESSAGE()),
      password: z
        .string()
        .min(6, VM.MIN_LENGTH_VALIDATION_MESSAGE(6))
        .max(64, VM.MAX_LENGTH_VALIDATION_MESSAGE(64))
        // Without a message zod shows its generic English "Invalid string" error.
        .regex(/[A-Z]/, passwordRuleMessage)
        .regex(/[0-9]/, passwordRuleMessage),
      // The match with `password` is checked below; a separate min(8) contradicted the password min(6).
      password_confirmation: z.string().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),

      address: z.string().optional(),
      street_number: z.string().optional(),
      postcode: z.string().optional(),
      city: z.string().optional(),
      country: z
        .object({
          id: z.string().or(z.number()).optional(),
        })
        .optional(),

      company_buy: z.boolean().optional(),
      company: z.string().optional(),
      ico: z.string().optional(),
      dic: z.string().optional(),
      ic_dph: z.string().optional(),
      email_company: z.string().optional(),
      address_company: z.string().optional(),
      street_number_company: z.string().optional(),
      postcode_company: z.string().optional(),
      city_company: z.string().optional(),
      country_company: z
        .object({
          id: z.string(),
        })
        .optional(),

      gdpr: z.boolean().refine(val => val === true, { message: VM.GDPR_VALIDATION_MESSAGE() }),
      newsletter: z.boolean().optional(),

      newsletter_return_url: z.string(),
      tos: z.boolean().refine(val => val === true, { message: VM.TOS_VALIDATION_MESSAGE() }),
      'g-recaptcha-response': z.string().default(''),
    })
    .refine(data => data.password === data.password_confirmation, {
      message: VM.PASSWORD_CONFIRMATION_DONT_MATCH_VALIDATION_MESSAGE(),
      path: ['password_confirmation'],
    });

export function RegisterForm({
  initialSocialAuthProviders,
  onShowSignIn,
  inDialog = false,
}: {
  initialSocialAuthProviders?: SocialAuthProviderItem[];
  onShowSignIn?: () => void;
  inDialog?: boolean;
}) {
  const { t } = useTranslations();

  const locale = useLocaleStore(s => s.currentLocale);

  const { formValidationMessages } = useFormValidationMessages(t);

  const { data: countries = { data: [] }, isLoading: isCountriesLoading } = useQuery({
    queryKey: [CacheKey.COUNTRIES, locale],
    queryFn: () => fetchCountries({ locale }),
    refetchOnWindowFocus: false,
  });

  const form = useForm<z.infer<ReturnType<typeof registerSchema>>>({
    resolver: zodResolver(registerSchema(formValidationMessages, t('text2349'))),
    defaultValues: {
      name: '',
      surname: '',
      email: '',
      phone: '',
      password: '',
      password_confirmation: '',
      gdpr: false,
      newsletter: false,
      tos: false,

      address: '',
      street_number: '',
      postcode: '',
      city: '',
      country: {
        id: 'SK',
      },

      company_buy: false,
      company: '',
      ico: '',
      dic: '',
      ic_dph: '',
      email_company: '',
      address_company: '',
      street_number_company: '',
      postcode_company: '',
      city_company: '',
      country_company: undefined,

      newsletter_return_url: new URL(
        ROUTES.NEWSLETTER_CONFIRM(),
        env.NEXT_PUBLIC_APP_URL,
      ).toString(),
    },
  });

  const router = useRouter();

  const [isSuccess, setIsSuccess] = useState(false);

  const handleSubmit = form.handleSubmit(async data => {
    if (data.password !== data.password_confirmation) {
      form.setError('password_confirmation', { type: 'manual', message: t('text468') });
      form.setError('password', { type: 'manual', message: t('text468') });
      return;
    }

    try {

      const resp = await register({ ...data,  locale });

      await setAuthToken({ authToken: resp.data.token });
      initCustomerStore({
        authToken: resp.data.token,
        customer: resp.data.customer,
      });

      if (inDialog) setIsSuccess(true);

      if (!inDialog) {
        toast({
          variant: 'success',
          title: `${t('text67')}`,
        });
        router.push(ROUTES.PROFILE());
      }
    } catch (e) {
      if (e instanceof ValidationError) {
        for (const [field, errors] of Object.entries(e.fields)) {
          form.setError(field as any, { type: 'manual', message: errors.join('\n') });
        }
        return;
      }
      // Network / server errors used to be swallowed and the button just stopped spinning.
      toast({ variant: 'destructive', title: t('text594'), description: t('text530') });
    }
  });

  if (isSuccess) {
    return <RegisterSuccess inDialog={inDialog} />;
  }

  return (
    <Form {...form}>
      <form
        className={cn(
          'flex w-full flex-col items-center justify-center gap-8',
          'ea-register-form',
          !inDialog && 'py-8 sm:py-12 lg:py-14',
        )}
        onSubmit={handleSubmit}
      >
        <div className="flex w-full flex-col items-center justify-center gap-6 sm:gap-8">
          <div className="relative flex w-full items-center justify-center">
            <Heading tag="h3" title={t('text261')} className={{ container: 'text-center' }} />
            {inDialog && (
              <DialogClose className="absolute right-0 top-1/2 -translate-y-1/2">
                <LuX size={20} className="text-border" />
              </DialogClose>
            )}
          </div>
          <Paragraph className="text-center tracking-tight text-[#666666]">
            {t('text623')}&nbsp;
            <button
              type="button"
              className={cn(
                'cursor-pointer font-semibold text-primary transition-all hover:opacity-70',
              )}
              onClick={inDialog ? onShowSignIn : () => router.push(ROUTES.SIGN_IN())}
            >
              {t('text624')}
            </button>
          </Paragraph>
        </div>

        <SocialAuthButtons
          className="ea-register-form__social-auth w-full"
          initialProviders={initialSocialAuthProviders}
          newsletterAccepted={form.watch('newsletter' as any)}
          newsletterReturnUrl={form.watch('newsletter_return_url' as any)}
        />

        <div
          className={cn(
            'flex w-full flex-col items-start justify-start gap-8 sm:gap-6',
            'ea-register-form__content',
          )}
        >
          <div className="grid w-full grid-cols-12 gap-x-2 gap-y-4 sm:gap-x-3 lg:gap-x-4">
            <FormTitle name={t('text385')} className="col-span-12" />
            <Field
              required
              type="email"
              name="email"
              label={'E-mail'}
              placeholder={'E-mail'}
              control={form.control}
              className={{ wrapper: 'col-span-12 w-full' }}
              before={<InputImage src="/images/icons/form/email.svg" alt="E-mail" />}
            />
            <Field
              required
              type="password"
              name="password"
              label={`${t('text191')}`}
              placeholder={`${t('text191')}`}
              control={form.control}
              className={{ wrapper: 'col-span-12 w-full sm:col-span-6' }}
              before={<InputImage src="/images/icons/form/password.svg" alt="Password" />}
              description={t('text2349')}
            />
            <Field
              required
              type="password"
              name="password_confirmation"
              label={`${t('text3183')}`}
              placeholder={`${t('text3183')}`}
              control={form.control}
              className={{ wrapper: 'col-span-12 w-full sm:col-span-6' }}
              before={<InputImage src="/images/icons/form/password.svg" alt="Password" />}
            />
          </div>

          <Separator />

          <div className="grid w-full grid-cols-12 gap-x-2 gap-y-4 sm:gap-x-3 lg:gap-x-4">
            <FormTitle name={t('text3560')} className="col-span-12" />
            <FormField
              control={form.control}
              name="company_buy"
              render={({ field }) => (
                <FormItem
                  className={cn('ea-register-form__company-type-field', 'col-span-12 w-full')}
                >
                  <FormControl>
                    <div className="grid w-full grid-cols-2 gap-3">
                      <button
                        type="button"
                        aria-pressed={field.value === false}
                        onClick={() => field.onChange(false)}
                        className={cn(
                          'col-span-1 flex w-full cursor-pointer flex-row items-center justify-start gap-3 rounded-[12px] p-4 text-sm font-medium tracking-tight text-black sm:text-base',
                          field.value === false
                            ? 'border-2 border-primary bg-primary-foreground'
                            : 'border border-border',
                        )}
                      >
                        <Image
                          src={'/images/icons/form/company-false.svg'}
                          alt={t('text2234')}
                          width={24}
                          height={24}
                          className="shrink-0"
                        />
                        <p>{t('text2234')}</p>
                      </button>

                      <button
                        type="button"
                        aria-pressed={field.value === true}
                        onClick={() => field.onChange(true)}
                        className={cn(
                          'col-span-1 flex w-full cursor-pointer flex-row items-center justify-start gap-3 rounded-[12px] p-4 text-sm font-medium tracking-tight text-black sm:text-base',
                          field.value === true
                            ? 'border-2 border-primary bg-primary-foreground'
                            : 'border border-border',
                        )}
                      >
                        <Image
                          src={'/images/icons/form/company-true.svg'}
                          alt={t('text1432')}
                          width={24}
                          height={24}
                          className="shrink-0"
                        />
                        <p>{t('text1432')}</p>
                      </button>
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            {form.watch('company_buy') === true ? (
              <CompanyFields
                countries={countries.data}
                isCountriesLoading={isCountriesLoading}
                form={form}
              />
            ) : (
              <PersonFields
                countries={countries.data}
                isCountriesLoading={isCountriesLoading}
                form={form}
              />
            )}
          </div>

          <Separator />

          <div className="col-span-12 grid w-full grid-cols-12 flex-col items-start justify-start gap-4 gap-x-3 gap-y-4">
            <FormTitle name={t('text803')} className="col-span-12" />
            {form.watch('company_buy') === true && (
              <Field
                name="email_company"
                type="email"
                label={'Email *'}
                placeholder={'Email'}
                control={form.control}
                className={{ wrapper: 'col-span-12 w-full' }}
              />
            )}
            {/* <Field
              name="name"
              label={`${t('text33')} *`}
              placeholder={`${t('text33')}`}
              control={form.control}
              className={{ wrapper: 'col-span-12 w-full sm:col-span-6' }}
            />
            <Field
              name="surname"
              label={`${t('text34')} *`}
              placeholder={`${t('text34')}`}
              control={form.control}
              className={{ wrapper: 'col-span-12 w-full sm:col-span-6' }}
            /> */}
            <Field
              required
              name="phone"
              label={`${t('text36')}`}
              placeholder={`${t('text36')}`}
              control={form.control}
              className={{ wrapper: 'col-span-12 w-full' }}
              type="tel"
            />
            {form.watch('company_buy') === true && (
              <>
                <Separator className="col-span-12 my-2 w-full" />
                <div className="col-span-12 grid w-full grid-cols-12 flex-col items-start justify-start gap-4 gap-x-2 gap-y-4 md:gap-x-3">
                  <FormTitle name={t('text1724')} className="col-span-12" />
                  <Field
                    name="name"
                    label={`${t('text33')} *`}
                    placeholder={`${t('text33')}`}
                    control={form.control}
                    className={{ wrapper: 'col-span-12 w-full sm:col-span-6' }}
                  />
                  <Field
                    name="surname"
                    label={`${t('text34')} *`}
                    placeholder={`${t('text34')}`}
                    control={form.control}
                    className={{ wrapper: 'col-span-12 w-full sm:col-span-6' }}
                  />
                </div>
              </>
            )}
          </div>
        </div>
        <div
          className={cn(
            'flex w-full flex-col items-start justify-start gap-4',
            'ea-register-form__footer',
          )}
        >
          <FormField
            control={form.control}
            name="tos"
            render={({ field }) => (
              <FormItem
                className={cn(
                  'flex flex-row flex-wrap items-center justify-start gap-0',
                  'ea-register-form__tos-field',
                )}
              >
                <FormControl>
                  <Checkbox
                    ref={field.ref}
                    name={field.name}
                    disabled={field.disabled}
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
                <FormLabel className="font-medium">
                  {t('text264')}{' '}
                  <Link
                    href={getDefaultUrlForTosOrGdprLocalized(ROUTES?.TOS?.(), locale, 'tos')}
                    className="font-bold text-primary underline"
                    prefetch={false}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {t('text265')}
                  </Link>
                </FormLabel>
                <FormMessage className="w-full pt-1" />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="gdpr"
            render={({ field }) => (
              <FormItem
                className={cn(
                  'flex flex-row flex-wrap items-center justify-start gap-0',
                  'ea-register-form__gdpr-field',
                )}
              >
                <FormControl>
                  <Checkbox
                    ref={field.ref}
                    name={field.name}
                    disabled={field.disabled}
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
                <FormLabel>
                  <Link
                    href={getDefaultUrlForTosOrGdprLocalized(
                      ROUTES?.GDPR?.() ?? '/',
                      locale,
                      'gdpr',
                    )}
                    className="font-bold text-primary underline"
                    prefetch={false}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {t('text364')}
                  </Link>
                </FormLabel>
                <FormMessage className="w-full pt-1" />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name={'g-recaptcha-response'}
            render={() => (
              // `hidden` also hid server-side captcha errors; hide only while empty.
              <FormItem required className={cn('w-full empty:hidden')}>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="mt-2 flex w-full items-center justify-center">
            <Button
              type="submit"
              className={cn('h-12 w-full', 'ea-register-form__submit-button')}
              isLoading={form.formState.isSubmitting}
            >
              {t('text758')}
            </Button>
          </div>
        </div>
      </form>
    </Form>
  );
}

function FormTitle({ name, className }: { name: string; className?: string }) {
  return <Heading className={{ container: className }} title={name} tag="h6" />;
}

function PersonFields({
  form,
  countries,
  isCountriesLoading,
}: {
  form: UseFormReturn<RegisterFormValues>;
  countries: Country[];
  isCountriesLoading: boolean;
}) {
  const { t } = useTranslations();

  return (
    <>
      <Field
        name="name"
        label={`${t('text33')} *`}
        placeholder={`${t('text33')}`}
        control={form.control}
        className={{ wrapper: 'col-span-12 w-full sm:col-span-6' }}
      />
      <Field
        name="surname"
        label={`${t('text34')} *`}
        placeholder={`${t('text34')}`}
        control={form.control}
        className={{ wrapper: 'col-span-12 w-full sm:col-span-6' }}
      />
      <Field
        name="address"
        label={`${t('text157')} *`}
        placeholder={`${t('text157')}`}
        control={form.control}
        className={{ wrapper: 'col-span-12 w-full sm:col-span-8' }}
      />
      <Field
        name="street_number"
        label={`${t('text438')} *`}
        placeholder={`${t('text438')}`}
        control={form.control}
        className={{ wrapper: 'col-span-12 w-full sm:col-span-4' }}
      />
      <Field
        name="postcode"
        label={`${t('text40')} *`}
        placeholder={`${t('text40')}`}
        control={form.control}
        className={{ wrapper: 'col-span-12 w-full sm:col-span-3' }}
      />
      <Field
        name="city"
        label={`${t('text39')} *`}
        placeholder={`${t('text39')}`}
        control={form.control}
        className={{ wrapper: 'col-span-12 w-full sm:col-span-9' }}
      />
      <Field
        name="country.id"
        type="select"
        label={`${t('text519')} *`}
        placeholder={`${t('text519')}`}
        control={form.control}
        className={{ wrapper: 'col-span-12 w-full' }}
        isDisabled={isCountriesLoading}
        options={countries.map(c => ({
          value: c.id,
          label: c.name,
        }))}
      />
    </>
  );
}

function CompanyFields({
  form,
  countries,
  isCountriesLoading,
}: {
  form: UseFormReturn<RegisterFormValues>;
  countries: Country[];
  isCountriesLoading: boolean;
}) {
  const { t } = useTranslations();

  return (
    <>
      <Field
        required
        name="company"
        label={`${t('text1188')}`}
        placeholder={`${t('text1188')}`}
        control={form.control}
        className={{ wrapper: 'col-span-12 w-full' }}
      />
      <Field
        required
        name="ico"
        label={`${t('text42')}`}
        placeholder={`${t('text42')}`}
        control={form.control}
        className={{ wrapper: 'col-span-12 w-full sm:col-span-6' }}
      />
      <Field
        name="dic"
        label={`${t('text43')}`}
        placeholder={`${t('text43')}`}
        control={form.control}
        className={{ wrapper: 'col-span-12 w-full sm:col-span-6' }}
      />
      <Field
        name="ic_dph"
        label={`${t('text44')}`}
        placeholder={`${t('text44')}`}
        control={form.control}
        className={{ wrapper: 'col-span-12 w-full' }}
      />
      <Field
        required
        name="address_company"
        label={`${t('text38')}`}
        placeholder={`${t('text38')}`}
        control={form.control}
        className={{ wrapper: 'col-span-12 w-full sm:col-span-8' }}
      />
      <Field
        required
        name="street_number_company"
        label={`${t('text438')}`}
        placeholder={`${t('text438')}`}
        control={form.control}
        className={{ wrapper: 'col-span-12 w-full sm:col-span-4' }}
      />
      <Field
        name="postcode_company"
        label={`${t('text40')}`}
        placeholder={`${t('text40')}`}
        control={form.control}
        className={{ wrapper: 'col-span-12 w-full sm:col-span-3' }}
      />
      <Field
        name="city_company"
        label={`${t('text39')}`}
        placeholder={`${t('text39')}`}
        control={form.control}
        className={{ wrapper: 'col-span-12 w-full sm:col-span-9' }}
      />
      <Field
        required
        control={form.control}
        type="select"
        name="country_company.id"
        label={t('text519')}
        className={{ wrapper: 'col-span-12 w-full' }}
        isDisabled={isCountriesLoading}
        options={countries.map(c => ({
          value: c.id,
          label: c.name,
        }))}
      />
    </>
  );
}
